import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { useApp, apiFetch } from "../../context/AppContext";
import { FORM_SECTIONS } from "../../data/formQuestionSections";
import DepartmentDropdown from "../../components/common/DepartmentSelect";
import DepartmentSelectionRequired from "../../components/common/DepartmentSelectionRequired";
import { AttachmentPreviewRow } from "../../components/common/UI";
import { tprmAttachmentPreviewPath } from "../../components/common/gapRecommendationUtils";
import { SuggestedEvidenceTrigger } from "../../components/common/GapQuestionComment";
import { useDepartments, useAutoSelectAssignedDepartment } from "../../hooks/useDepartments";
import { useAssessmentCycles } from "../../hooks/useAssessmentCycles";
import {
  getLiveTourPreviewDepartmentId,
  notifyLiveTourTargetsChanged,
  useLiveTourDepartmentPrep,
  useLiveTourOptional,
} from "../../context/LiveTourContext";
import {
  afterValidationRender,
  isAttachmentBlocking,
  isFieldRequired,
  questionHintStyle,
  scrollToFirstInvalidQuestion,
  shouldEnforceAttachment,
} from "../../utils/formValidationFocus";
import {
  ShieldCheck,
  CheckCircle2,
  Check,
  X,
  ChevronRight,
  ChevronLeft,
  Loader2,
  AlertTriangle,
  FileText,
  Star,
  Activity,
  Shield,
  Globe,
  Lock,
  Building2,
  BarChart3,
  TrendingUp,
  Award,
  LayoutList,
  ClipboardList,
  Zap,
  Paperclip,
} from "lucide-react";

const TPRM_ATTACHMENT_ACCEPT =
  ".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.webp,.gif,.zip";

function qKey(id) {
  return String(id ?? "");
}

function groupAttachmentsByQuestion(rows) {
  const grouped = {};
  if (!Array.isArray(rows)) return grouped;
  rows.forEach((row) => {
    const key = qKey(row.question_id);
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(row);
  });
  return grouped;
}

function TprmQuestionAttachment({
  questionId,
  attachments = [],
  uploading = false,
  onUpload,
  onRemove,
  removingId = null,
  attachmentRequired = false,
  disabled = false,
  error = "",
}) {
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !onUpload) return;
    try {
      await onUpload(questionId, file);
    } finally {
      e.target.value = "";
    }
  };

  return (
    <div style={{ marginTop: 12 }}>
      <label
        style={{
          display: "block",
          fontSize: 11,
          fontWeight: 600,
          color: "#334155",
          marginBottom: 6,
        }}
      >
        Supporting Attachment{" "}
        {attachmentRequired && <span style={{ color: "#ef4444" }}>*</span>}
      </label>
      <input
        ref={fileRef}
        type="file"
        accept={TPRM_ATTACHMENT_ACCEPT}
        style={{ display: "none" }}
        disabled={disabled}
        onChange={handleFile}
      />
      <button
        type="button"
        onClick={() => {
          if (disabled) return;
          fileRef.current?.click();
        }}
        disabled={disabled || uploading}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "8px 14px",
          borderRadius: 10,
          border: attachmentRequired
            ? "1.5px dashed #f59e0b"
            : "1px solid rgba(99,102,241,0.2)",
          background: attachmentRequired ? "#fffbeb" : "#ffffff",
          color: attachmentRequired ? "#92400e" : "#6366f1",
          fontSize: 12,
          fontWeight: 600,
          cursor: disabled || uploading ? "not-allowed" : "pointer",
          opacity: disabled || uploading ? 0.65 : 1,
          fontFamily: "inherit",
        }}
      >
        {uploading ? (
          <Loader2 size={14} className="animate-spin" />
        ) : (
          <Paperclip size={14} />
        )}
        {attachmentRequired ? "Attach required file *" : "Attach file"}
      </button>
      <span
        style={{
          display: "block",
          fontSize: 10,
          color: "#94a3b8",
          marginTop: 6,
        }}
      >
        PDF, Office docs, CSV, images, or ZIP
      </span>
      {error && (
        <p
          style={{
            ...questionHintStyle,
            marginTop: 8,
            color: "#ef4444",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <AlertTriangle size={14} color="#ef4444" />
          {error}
        </p>
      )}
      {attachments.length > 0 && (
        <div className="space-y-2 mt-2">
          {attachments.map((file) => (
            <AttachmentPreviewRow
              key={file.id}
              file={file}
              previewPath={tprmAttachmentPreviewPath(file)}
              onRemove={disabled ? undefined : () => onRemove?.(questionId, file)}
              removing={removingId === file.id}
            />
          ))}
        </div>
      )}
      {attachmentRequired && attachments.length === 0 && !error && (
        <p className="text-xs text-amber-600" style={{ marginTop: 8 }}>
          This question requires an attachment before submission.
        </p>
      )}
    </div>
  );
}

const API = process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

// Canonical section order — matches the tenant admin's Questionnaire manager
// (FORM_SECTIONS.tprm) so the assessment form and admin config stay in sync.
const TPRM_CATEGORY_ORDER = FORM_SECTIONS.tprm.map((s) => s.id);

// Questions are loaded from the backend (tenant custom or platform defaults).

// ΓöÇΓöÇΓöÇ Helpers ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
function getVendorInfo() {
  const token = localStorage.getItem("vendor_token");
  const slug = localStorage.getItem("vendor_tenant_slug");
  const id = localStorage.getItem("vendor_id");
  return { token, slug, id };
}

function parseApiError(data, fallback = "Request failed") {
  if (!data) return fallback;
  if (Array.isArray(data.detail)) {
    return data.detail
      .map(
        (d) =>
          d.msg || d.message || (typeof d === "string" ? d : JSON.stringify(d)),
      )
      .join(", ");
  }
  return data.detail || fallback;
}

function normalizeTprmQuestion(q) {
  return {
    ...q,
    required: q.required === false ? false : Boolean(q.required),
    allow_attachment: Boolean(q.allow_attachment),
    attachment_required: Boolean(q.attachment_required),
  };
}

function sortTprmQuestions(questions) {
  return [...questions].sort(
    (a, b) =>
      (a.order || 0) - (b.order || 0) ||
      String(a.id).localeCompare(String(b.id)),
  );
}

function isAnswered(value) {
  if (value === false || value === 0) return true;
  if (Array.isArray(value)) return value.length > 0;
  return value !== undefined && value !== null && String(value).trim() !== "";
}

const BOOL_CFG = {
  Yes: { color: "#10b981", bg: "#ecfdf5", border: "#6ee7b7", icon: Check },
  No: { color: "#ef4444", bg: "#fef2f2", border: "#fca5a5", icon: X },
};

function getCategoryMeta(category) {
  const map = {
    general: {
      title: "General Information",
      icon: Star,
      color: "bg-gradient-to-br from-blue-600 to-blue-400 text-white",
      accent: "#3b82f6",
    },
    security: {
      title: "Cybersecurity & Data",
      icon: Lock,
      color: "bg-gradient-to-br from-violet-600 to-violet-400 text-white",
      accent: "#8b5cf6",
    },
    compliance: {
      title: "Compliance & Regulatory",
      icon: Shield,
      color: "bg-gradient-to-br from-emerald-600 to-emerald-400 text-white",
      accent: "#10b981",
    },
    financial: {
      title: "Financial Stability",
      icon: BarChart3,
      color: "bg-gradient-to-br from-amber-600 to-amber-400 text-white",
      accent: "#f59e0b",
    },
    operational: {
      title: "Operational Risk",
      icon: Globe,
      color: "bg-gradient-to-br from-rose-600 to-rose-400 text-white",
      accent: "#f43f5e",
    },
    legal: {
      title: "Legal",
      icon: Building2,
      color: "bg-gradient-to-br from-cyan-600 to-cyan-400 text-white",
      accent: "#06b6d4",
    },
    reputation: {
      title: "Reputation",
      icon: Activity,
      color: "bg-gradient-to-br from-pink-600 to-pink-400 text-white",
      accent: "#ec4899",
    },
  };
  return (
    map[category] || {
      title: category
        ? category.charAt(0).toUpperCase() + category.slice(1)
        : "General",
      icon: FileText,
      color: "bg-gradient-to-br from-slate-900 to-slate-600 text-white",
      accent: "#64748b",
    }
  );
}

function getQuestionIcon(type) {
  return (
    {
      boolean: CheckCircle2,
      text: FileText,
      textarea: FileText,
      rating: Star,
      select: ChevronRight,
      multiselect: Activity,
      date: Activity,
      file: FileText,
    }[type] || FileText
  );
}

function getOptionLabel(opt) {
  if (opt && typeof opt === "object") {
    return opt.label ?? String(opt);
  }
  return String(opt);
}

function getOptionValue(opt) {
  if (opt && typeof opt === "object") {
    return opt.label ?? JSON.stringify(opt);
  }
  return opt;
}

function normalizeAnswerValue(answer) {
  if (Array.isArray(answer)) {
    return answer.map((item) => normalizeAnswerValue(item));
  }
  if (answer && typeof answer === "object") {
    return answer.label ?? JSON.stringify(answer);
  }
  return answer;
}

function detectLocked(draft) {
  if (!draft) return false;
  return Boolean(
    draft.is_locked ??
    draft.is_submitted ??
    draft.submitted ??
    (draft.status && String(draft.status).toLowerCase() === "submitted"),
  );
}

function normalizeAnswers(answerMap) {
  return Object.keys(answerMap).reduce((memo, key) => {
    memo[key] = normalizeAnswerValue(answerMap[key]);
    return memo;
  }, {});
}

// ΓöÇΓöÇΓöÇ Score calculation ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
// Returns 0ΓÇô100 based on required questions answered in a set
function calcScore(questions, answers, attachments) {
  const required = questions.filter((q) => isFieldRequired(q.required, false));
  if (!required.length) return 100;
  let earned = 0;
  required.forEach((q) => {
    const hasAnswer = isAnswered(answers[q.id]);
    const hasAtt = !isAttachmentBlocking(q, attachments, q.id);
    if (hasAnswer && hasAtt) earned++;
  });
  return Math.round((earned / required.length) * 100);
}

function getRiskBadge(score) {
  if (score >= 80)
    return {
      label: "Low Risk",
      bg: "bg-emerald-50",
      text: "text-emerald-700",
      border: "border-emerald-200",
      dot: "bg-emerald-500",
      bar: "bg-emerald-500",
    };
  if (score >= 50)
    return {
      label: "Medium Risk",
      bg: "bg-amber-50",
      text: "text-amber-700",
      border: "border-amber-200",
      dot: "bg-amber-500",
      bar: "bg-amber-500",
    };
  if (score >= 20)
    return {
      label: "High Risk",
      bg: "bg-rose-50",
      text: "text-rose-700",
      border: "border-rose-200",
      dot: "bg-rose-500",
      bar: "bg-rose-500",
    };
  return {
    label: "Critical Risk",
    bg: "bg-red-50",
    text: "text-red-700",
    border: "border-red-200",
    dot: "bg-red-600",
    bar: "bg-red-600",
  };
}

// ΓöÇΓöÇΓöÇ Score Arc SVG ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
function ScoreArc({ score }) {
  const r = 36;
  const circ = 2 * Math.PI * r;
  // Only fill 75% of the circle (270┬░) like a speedometer
  const arcLen = circ * 0.75;
  const filled = arcLen * (score / 100);
  const badge = getRiskBadge(score);
  const color =
    score >= 80
      ? "#10b981"
      : score >= 50
        ? "#f59e0b"
        : score >= 20
          ? "#f43f5e"
          : "#dc2626";

  return (
    <div
      className="relative flex items-center justify-center"
      style={{ width: 88, height: 88 }}
    >
      <svg width="88" height="88" viewBox="0 0 88 88">
        {/* Background arc */}
        <circle
          cx="44"
          cy="44"
          r={r}
          fill="none"
          stroke="rgba(148,163,184,0.15)"
          strokeWidth="8"
          strokeDasharray={`${arcLen} ${circ}`}
          strokeDashoffset={`${circ * 0.125}`}
          strokeLinecap="round"
          transform="rotate(135 44 44)"
        />
        {/* Filled arc */}
        <circle
          cx="44"
          cy="44"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="8"
          strokeDasharray={`${filled} ${circ}`}
          strokeDashoffset={`${circ * 0.125}`}
          strokeLinecap="round"
          transform="rotate(135 44 44)"
          style={{
            transition:
              "stroke-dasharray 0.9s cubic-bezier(0.4,0,0.2,1), stroke 0.4s",
          }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[18px] font-black text-slate-900 leading-none">
          {score}
        </span>
        <span className="text-[9px] font-bold text-slate-600 uppercase tracking-wider mt-0.5">
          Score
        </span>
      </div>
    </div>
  );
}

// ΓöÇΓöÇΓöÇ Section Score Row ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
function SectionScoreRow({ section, score }) {
  const meta = getCategoryMeta(section.id);
  const badge = getRiskBadge(score);
  const Icon = section.icon;

  return (
    <div className="flex items-center gap-2 px-1">
      <div
        className="w-5 h-5 rounded-md flex items-center justify-center shrink-0"
        style={{ background: `${meta.accent}18` }}
      >
        <Icon size={11} style={{ color: meta.accent }} />
      </div>
      <span className="text-[10.5px] text-slate-600 flex-1 leading-tight truncate">
        {section.title}
      </span>
      <div className="flex items-center gap-1.5 shrink-0">
        <div className="w-16 h-1.5 bg-slate-400 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-700 ${badge.bar}`}
            style={{ width: `${score}%` }}
          />
        </div>
        <span
          className={`text-[10px] font-bold tabular-nums w-7 text-right ${badge.text}`}
        >
          {score}%
        </span>
      </div>
    </div>
  );
}

function buildResultSections(sectionScores) {
  if (!sectionScores || typeof sectionScores !== "object") return [];
  const ordered = [
    ...TPRM_CATEGORY_ORDER.filter((cat) => sectionScores[cat] != null),
    ...Object.keys(sectionScores).filter(
      (cat) => !TPRM_CATEGORY_ORDER.includes(cat),
    ),
  ];
  return ordered.map((cat) => {
    const meta = getCategoryMeta(cat);
    return {
      id: cat,
      title: meta.title,
      icon: meta.icon,
      accent: meta.accent,
      score: Number(sectionScores[cat]) || 0,
    };
  });
}

function TprmSubmitResultScreen({
  submitResult,
  departmentLabel = "",
  onViewSubmissions,
}) {
  const overall = Number(submitResult?.overall_score) || 0;
  const badge = getRiskBadge(overall);
  const resultSections = buildResultSections(submitResult?.section_scores);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center p-6">
      <div className="bg-white border border-slate-200 rounded-3xl shadow-[0_20px_60px_rgba(15,23,42,0.08)] max-w-lg w-full overflow-hidden">
        <div className="p-8 pb-6 text-center border-b border-slate-100">
          <div className="w-16 h-16 rounded-2xl bg-indigo-50 flex items-center justify-center mx-auto border border-indigo-100 mb-4">
            <Award size={32} className="text-indigo-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-1">
            Assessment Submitted
          </h2>
          <p className="text-slate-600 text-sm leading-6">
            Your TPRM assessment has been submitted successfully.
            {departmentLabel ? (
              <>
                {" "}
                <span className="font-semibold text-slate-800">
                  {departmentLabel}
                </span>
              </>
            ) : null}
          </p>
        </div>

        <div className="p-8 pt-6 space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
            <p
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#334155",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                marginBottom: 12,
                textAlign: "center",
              }}
            >
              Overall Score
            </p>
            <div className="flex items-center justify-center gap-5">
              <ScoreArc score={overall} />
              <div className="text-left">
                <span
                  className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1 rounded-full border ${badge.bg} ${badge.text} ${badge.border}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`} />
                  {badge.label}
                </span>
                <p className="text-3xl font-black text-slate-900 mt-2 mb-0 leading-none">
                  {overall}%
                </p>
                <p className="text-[11px] text-slate-500 mt-1 mb-0">
                  Weighted compliance score
                </p>
              </div>
            </div>
          </div>

          {resultSections.length > 0 && (
            <div className="rounded-2xl border border-slate-200 p-5">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 size={14} className="text-indigo-600" />
                <p
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#0f172a",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    margin: 0,
                  }}
                >
                  Section Score Breakdown
                </p>
              </div>
              <div className="flex flex-col gap-3">
                {resultSections.map((sec) => (
                  <SectionScoreRow
                    key={sec.id}
                    section={sec}
                    score={sec.score}
                  />
                ))}
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={onViewSubmissions}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold py-3 transition-colors"
          >
            <ClipboardList size={16} />
            View My Submissions
          </button>
        </div>
      </div>
    </div>
  );
}

// ΓöÇΓöÇΓöÇ Main component ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
export default function TPRMAssessment() {
  const navigate = useNavigate();
  const { currentUser } = useApp();

  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [attachments, setAttachments] = useState({});
  const [referenceEvidence, setReferenceEvidence] = useState({});
  const [uploadingFor, setUploadingFor] = useState({});
  const [usingReferenceFor, setUsingReferenceFor] = useState({});
  const [removingAttachmentId, setRemovingAttachmentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [error, setError] = useState("");
  const [attachmentErrors, setAttachmentErrors] = useState({});
  const [step, setStep] = useState(0);
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftSaving, setDraftSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [departmentId, setDepartmentId] = useState("");
  const [invalidQuestions, setInvalidQuestions] = useState([]);
  const [isLocked, setIsLocked] = useState(false);
  const [lockInfo, setLockInfo] = useState(null);
  const questionScrollRef = useRef(null);
  const { departments, loading: departmentsLoading } = useDepartments();
  const { currentCycleId, currentCycle } = useAssessmentCycles("tprm");
  const isCycleClosed =
    String(currentCycle?.status || "open").toLowerCase() === "closed";
  const formReadOnly = isLocked || isCycleClosed;
  const { token, slug, id } = getVendorInfo();
  const isVendorFlow = Boolean(token && slug && id);
  useAutoSelectAssignedDepartment({
    departmentId,
    setDepartmentId,
    departments,
    loading: departmentsLoading,
    enabled: Boolean(currentUser && !isVendorFlow),
  });
  const liveTour = useLiveTourOptional();
  const tourPreviewDepartmentId = getLiveTourPreviewDepartmentId(
    liveTour,
    departments,
    departmentsLoading,
  );
  const activeDepartmentId = departmentId || tourPreviewDepartmentId;
  useLiveTourDepartmentPrep(departmentId, setDepartmentId, departments, departmentsLoading, {
    enabled: Boolean(currentUser && !isVendorFlow),
  });

  useEffect(() => {
    let cancelled = false;
    const loadQuestionnaire = async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        if (currentUser && !isVendorFlow) {
          const res = await apiFetch("/tprm/questionnaire");
          if (!cancelled && res.ok) {
            const data = await res.json();
            setQuestions(
              sortTprmQuestions(
                (data.items || []).map(normalizeTprmQuestion),
              ),
            );
          }
        } else if (isVendorFlow) {
          const res = await fetch(
            `${API}/vendor-auth/questionnaire?tenant_slug=${encodeURIComponent(slug)}`,
          );
          const data = await res.json().catch(() => ({}));
          if (!cancelled && res.ok) {
            setQuestions(
              sortTprmQuestions(
                (data.items || []).map(normalizeTprmQuestion),
              ),
            );
          }
        }
      } catch {
        // keep empty — backend should always return defaults when reachable
      } finally {
        if (!cancelled && !silent) setLoading(false);
      }
    };
    loadQuestionnaire(false);
    const onFocus = () => {
      // Background refresh only — avoid flashing the full-page loader when the
      // window regains focus (e.g. after closing the native file picker dialog
      // used for attachment uploads), since that made the page look reloaded.
      if (currentUser && !isVendorFlow) loadQuestionnaire(true);
    };
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
    };
  }, [currentUser, isVendorFlow, slug]);

  useEffect(() => {
    if (!currentUser || isVendorFlow || !questions.length) return undefined;
    const ids = [
      ...new Set(
        questions.flatMap((q) => [q.id, q.external_id]).filter(Boolean),
      ),
    ];
    if (!ids.length) return undefined;
    const params = new URLSearchParams({ question_ids: ids.join(",") });
    const loadEvidence = () =>
      apiFetch(`/tprm/reference-evidence?${params}`)
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (data?.items) setReferenceEvidence(data.items);
        })
        .catch(() => {});
    loadEvidence();
    return undefined;
  }, [questions, currentUser, isVendorFlow]);

  useEffect(() => {
    const hasPending = Object.values(referenceEvidence).some((item) =>
      ["pending", "generating"].includes(item?.generation_status),
    );
    if (!currentUser || isVendorFlow || !questions.length || !hasPending) {
      return undefined;
    }
    const ids = [
      ...new Set(
        questions.flatMap((q) => [q.id, q.external_id]).filter(Boolean),
      ),
    ];
    if (!ids.length) return undefined;
    const params = new URLSearchParams({ question_ids: ids.join(",") });
    const timer = setInterval(() => {
      apiFetch(`/tprm/reference-evidence?${params}`)
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (data?.items) setReferenceEvidence(data.items);
        })
        .catch(() => {});
    }, 8000);
    return () => clearInterval(timer);
  }, [questions, currentUser, isVendorFlow, referenceEvidence]);

  const saveTimer = useRef(null);

  const handleDepartmentChange = (e) => {
    clearTimeout(saveTimer.current);
    setDraftLoading(true);
    setDepartmentId(e.target.value);
  };

  const loadDepartmentAssessment = useCallback(
    async (deptId) => {
      if (!currentUser || isVendorFlow) return;
      if (!deptId) {
        setAnswers({});
        setAttachments({});
        setLastSavedAt(null);
        setIsLocked(false);
        setLockInfo(null);
        setDraftLoading(false);
        return;
      }
      setDraftLoading(true);
      try {
        const q = encodeURIComponent(deptId);
        const cycleQ = currentCycleId
          ? `&cycle_id=${encodeURIComponent(currentCycleId)}`
          : "";
        const [draftRes, attRes] = await Promise.all([
          apiFetch(`/tprm/draft?department_id=${q}${cycleQ}`),
          apiFetch(`/tprm/attachments?department_id=${q}`),
        ]);
        if (draftRes.ok) {
          const data = await draftRes.json();
          const locked = detectLocked(data);
          setIsLocked(locked);
          setLockInfo(
            locked
              ? {
                  submittedAt: data.submitted_at || data.updated_at || null,
                  submittedBy:
                    data.submitted_by_name || data.submitted_by || null,
                }
              : null,
          );
          setAnswers(normalizeAnswers(data.answers || {}));
          if (data.updated_at) setLastSavedAt(data.updated_at);
        } else {
          setAnswers({});
          setLastSavedAt(null);
          setIsLocked(false);
          setLockInfo(null);
        }
        const grouped = attRes.ok
          ? groupAttachmentsByQuestion(await attRes.json())
          : {};
        setAttachments(grouped);
        setAttachmentErrors({});
        setError("");
      } catch {
        setAnswers({});
        setAttachments({});
        setLastSavedAt(null);
        setIsLocked(false);
        setLockInfo(null);
      } finally {
        setDraftLoading(false);
      }
    },
    [currentUser, isVendorFlow, currentCycleId],
  );

  useEffect(() => {
    if (currentUser && !isVendorFlow) {
      loadDepartmentAssessment(activeDepartmentId);
    }
  }, [activeDepartmentId, currentUser, isVendorFlow, currentCycleId, loadDepartmentAssessment]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (currentUser && !isVendorFlow) return;
      setDraftLoading(true);
      try {
        if (isVendorFlow) {
          const res = await fetch(
            `${API}/vendor-auth/questionnaire/draft?tenant_slug=${encodeURIComponent(slug)}&vendor_id=${encodeURIComponent(id)}`,
          );
          const data = await res.json().catch(() => ({}));
          if (!cancelled && res.ok && data.answers) {
            setAnswers(normalizeAnswers(data.answers || {}));
            if (data.updated_at) setLastSavedAt(data.updated_at);
          }
        }
      } catch {
      } finally {
        if (!cancelled) setDraftLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser, isVendorFlow, slug, id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (currentUser && !isVendorFlow) return;
        let data = [];
        if (isVendorFlow) {
          const res = await fetch(
            `${API}/vendor-auth/questionnaire/attachments?tenant_slug=${encodeURIComponent(slug)}&vendor_id=${encodeURIComponent(id)}`,
          );
          data = await res.json().catch(() => []);
        }
        if (cancelled || !Array.isArray(data)) return;
        setAttachments(groupAttachmentsByQuestion(data));
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser, isVendorFlow, slug, id]);

  const sections = useMemo(() => {
    const grouped = {};
    questions.forEach((q) => {
      const key = q.category || "general";
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(q);
    });
    const orderedCategories = [
      ...TPRM_CATEGORY_ORDER.filter((cat) => grouped[cat]?.length),
      ...Object.keys(grouped).filter((cat) => !TPRM_CATEGORY_ORDER.includes(cat)),
    ];
    return orderedCategories.map((category) => {
      const meta = getCategoryMeta(category);
      return {
        id: category,
        title: meta.title,
        icon: meta.icon,
        color: meta.color,
        accent: meta.accent,
        questions: sortTprmQuestions(grouped[category]),
      };
    });
  }, [questions]);

  const totalQuestions = questions.length;
  const answeredCount = questions.filter((q) => {
    const hasAnswer = isAnswered(answers[q.id]);
    if (isFieldRequired(q.required, false)) {
      return hasAnswer && !isAttachmentBlocking(q, attachments, q.id);
    }
    return hasAnswer;
  }).length;
  const progress = totalQuestions
    ? Math.round((answeredCount / totalQuestions) * 100)
    : 0;

  // ΓöÇΓöÇ Overall score (based on required questions answered) ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
  const overallScore = calcScore(questions, answers, attachments);
  const overallBadge = getRiskBadge(overallScore);

  // Per-section scores
  const sectionScores = useMemo(
    () =>
      sections.reduce((acc, sec) => {
        acc[sec.id] = calcScore(sec.questions, answers, attachments);
        return acc;
      }, {}),
    [sections, answers, attachments],
  );

  const currentSection = sections[step] || sections[0];
  const secAnswered =
    currentSection?.questions.filter((q) => isAnswered(answers[q.id])).length ??
    0;
  const secTotal = currentSection?.questions.length ?? 0;
  const secPct = secTotal ? Math.round((secAnswered / secTotal) * 100) : 0;
  const handleAnswer = (qid, value) =>
    setAnswers((prev) => ({ ...prev, [qid]: value }));

  useEffect(() => {
    const onTourLastSection = () => {
      if (sections.length === 0) return;
      setStep(sections.length - 1);
      window.setTimeout(() => notifyLiveTourTargetsChanged(), 280);
    };
    window.addEventListener("live-tour:go-to-last-section", onTourLastSection);
    return () =>
      window.removeEventListener("live-tour:go-to-last-section", onTourLastSection);
  }, [sections.length]);

  useEffect(() => {
    if (draftLoading) return;
    if (!currentUser && !isVendorFlow) return;
    if (currentUser && !isVendorFlow && !activeDepartmentId) return;
    if (currentUser && !isVendorFlow && formReadOnly) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setDraftSaving(true);
      try {
        let res;
        if (currentUser && !isVendorFlow) {
          res = await apiFetch("/tprm/draft", {
            method: "POST",
            body: JSON.stringify({
              answers,
              department_id: activeDepartmentId || null,
              cycle_id: currentCycleId || null,
            }),
          });
        } else if (isVendorFlow) {
          res = await fetch(`${API}/vendor-auth/questionnaire/draft`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ tenant_slug: slug, vendor_id: id, answers }),
          });
        }
        if (res?.ok) {
          const data = await res.json().catch(() => ({}));
          setLastSavedAt(data.updated_at || new Date().toISOString());
        }
      } catch {
      } finally {
        setDraftSaving(false);
      }
    }, 800);
    return () => clearTimeout(saveTimer.current);
  }, [
    answers,
    activeDepartmentId,
    currentUser,
    isVendorFlow,
    slug,
    id,
    draftLoading,
    isLocked,
    isCycleClosed,
    currentCycleId,
  ]);

  const uploadAttachment = async (questionId, file) => {
    const key = qKey(questionId);
    const deptId = activeDepartmentId;
    const fd = new FormData();
    fd.append("question_id", key);
    fd.append("file", file);
    setUploadingFor((prev) => ({ ...prev, [key]: true }));
    setAttachmentErrors((prev) => ({ ...prev, [key]: "" }));
    try {
      let res;
      if (isVendorFlow) {
        fd.append("tenant_slug", slug);
        fd.append("vendor_id", id);
        res = await fetch(`${API}/vendor-auth/questionnaire/attachment`, {
          method: "POST",
          body: fd,
        });
      } else if (currentUser) {
        if (!deptId) {
          throw new Error("Select a department before uploading attachments");
        }
        if (formReadOnly) {
          throw new Error(
            "This assessment has already been submitted and is locked for editing.",
          );
        }
        fd.append("department_id", deptId);
        res = await apiFetch("/tprm/attachment", { method: "POST", body: fd });
      } else {
        throw new Error("Please log in to upload attachments");
      }
      let data = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }
      if (!res.ok) {
        const detail = data.detail;
        const msg = Array.isArray(detail)
          ? detail[0]?.msg || detail[0]
          : detail || "Attachment upload failed";
        throw new Error(msg);
      }
      setAttachments((prev) => ({
        ...prev,
        [key]: [...(prev[key] || []), data],
      }));
      setError("");
      setAttachmentErrors((prev) => ({ ...prev, [key]: "" }));
    } catch (err) {
      const msg = err.message || "Attachment upload failed";
      setError(msg);
      setAttachmentErrors((prev) => ({ ...prev, [key]: msg }));
      throw err;
    } finally {
      setUploadingFor((prev) => ({ ...prev, [key]: false }));
    }
  };

  const attachReferenceEvidence = async (questionId, evidence) => {
    const key = qKey(questionId);
    if (!activeDepartmentId) {
      throw new Error("Select a department before attaching evidence");
    }
    if (formReadOnly) {
      throw new Error("This assessment is locked for editing.");
    }
    setUsingReferenceFor((prev) => ({ ...prev, [key]: true }));
    setAttachmentErrors((prev) => ({ ...prev, [key]: "" }));
    try {
      const fd = new FormData();
      fd.append("reference_evidence_id", evidence.id);
      fd.append("question_id", key);
      fd.append("department_id", activeDepartmentId);
      const res = await apiFetch("/tprm/attachment/from-reference", {
        method: "POST",
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || "Could not attach suggested evidence");
      }
      setAttachments((prev) => ({
        ...prev,
        [key]: [...(prev[key] || []), data],
      }));
    } catch (err) {
      const msg = err.message || "Could not attach suggested evidence";
      setAttachmentErrors((prev) => ({ ...prev, [key]: msg }));
      throw err;
    } finally {
      setUsingReferenceFor((prev) => ({ ...prev, [key]: false }));
    }
  };

  const attachSuggestedEvidence = (questionId, evidence) =>
    attachReferenceEvidence(questionId, evidence);

  const removeAttachment = async (questionId, file) => {
    if (!file?.id) return;
    const key = qKey(questionId);
    if (formReadOnly) {
      setAttachmentErrors((prev) => ({
        ...prev,
        [key]: "This assessment has already been submitted and is locked for editing.",
      }));
      return;
    }
    setRemovingAttachmentId(file.id);
    setAttachmentErrors((prev) => ({ ...prev, [key]: "" }));
    try {
      let res;
      if (isVendorFlow) {
        const qs = new URLSearchParams({
          tenant_slug: slug,
          vendor_id: id,
          attachment_id: file.id,
        });
        res = await fetch(
          `${API}/vendor-auth/questionnaire/attachment?${qs.toString()}`,
          { method: "DELETE" },
        );
      } else if (currentUser) {
        res = await apiFetch(`/tprm/attachment/${file.id}`, {
          method: "DELETE",
        });
      } else {
        throw new Error("Please log in to remove attachments");
      }
      if (!res.ok) {
        let data = {};
        try {
          data = await res.json();
        } catch {
          data = {};
        }
        const detail = data.detail;
        const msg = Array.isArray(detail)
          ? detail[0]?.msg || detail[0]
          : detail || "Could not remove attachment";
        throw new Error(msg);
      }
      setAttachments((prev) => ({
        ...prev,
        [key]: (prev[key] || []).filter((f) => f.id !== file.id),
      }));
      setError("");
    } catch (err) {
      const msg = err.message || "Could not remove attachment";
      setError(msg);
      setAttachmentErrors((prev) => ({ ...prev, [key]: msg }));
    } finally {
      setRemovingAttachmentId(null);
    }
  };

  const unansweredRequired = questions.filter(
    (q) => isFieldRequired(q.required, false) && !isAnswered(answers[q.id]),
  );
  const missingRequiredAttachments = questions.filter(
    (q) =>
      shouldEnforceAttachment(q, answers, q.id) &&
      isAttachmentBlocking(q, attachments, q.id),
  );

  const getSectionValidationIssues = (section) => {
    const ids = [];
    section.questions.forEach((q) => {
      if (!isFieldRequired(q.required, false)) {
        if (
          shouldEnforceAttachment(q, answers, q.id) &&
          isAttachmentBlocking(q, attachments, q.id)
        ) {
          ids.push(q.id);
        }
        return;
      }
      if (!isAnswered(answers[q.id])) ids.push(q.id);
      else if (isAttachmentBlocking(q, attachments, q.id)) ids.push(q.id);
    });
    return ids;
  };

  const focusQuestionIssues = (questionIds, delay = 0) => {
    afterValidationRender(() => {
      setTimeout(
        () => scrollToFirstInvalidQuestion(questionIds, questionScrollRef),
        delay,
      );
    });
  };

  const handleNext = () => {
    if (formReadOnly) {
      setStep((s) => Math.min(sections.length - 1, s + 1));
      return;
    }
    const issues = getSectionValidationIssues(currentSection);
    if (issues.length) {
      setInvalidQuestions(issues);
      setError(
        `Please complete ${issues.length} required question(s) in this section.`,
      );
      focusQuestionIssues(issues);
      return;
    }
    setInvalidQuestions([]);
    setError("");
    setStep((s) => s + 1);
  };

  const handleSubmit = async () => {
    if (formReadOnly) {
      setError(
        isLocked
          ? "This TPRM assessment has already been submitted for this department and is locked for editing."
          : "This assessment cycle is closed. Contact your tenant admin to reopen it.",
      );
      return;
    }
    if (unansweredRequired.length > 0) {
      const firstId = unansweredRequired[0].id;
      const secIdx = sections.findIndex((s) =>
        s.questions.some((q) => q.id === firstId),
      );
      const jump = secIdx >= 0 && secIdx !== step;
      if (jump) setStep(secIdx);
      setInvalidQuestions(unansweredRequired.map((q) => q.id));
      setError(
        `Please answer all required questions (${unansweredRequired.length} remaining)`,
      );
      focusQuestionIssues(
        unansweredRequired.map((q) => q.id),
        jump ? 280 : 0,
      );
      return;
    }
    if (missingRequiredAttachments.length > 0) {
      const firstId = missingRequiredAttachments[0].id;
      const secIdx = sections.findIndex((s) =>
        s.questions.some((q) => q.id === firstId),
      );
      const jump = secIdx >= 0 && secIdx !== step;
      if (jump) setStep(secIdx);
      setInvalidQuestions(missingRequiredAttachments.map((q) => q.id));
      setError(
        `Please upload all required attachments (${missingRequiredAttachments.length} remaining)`,
      );
      focusQuestionIssues(
        missingRequiredAttachments.map((q) => q.id),
        jump ? 280 : 0,
      );
      return;
    }
    if (currentUser && !isVendorFlow && !activeDepartmentId) {
      setError("Please select your department before submitting.");
      return;
    }
    setError("");
    setInvalidQuestions([]);
    setSubmitting(true);
    try {
      let res;
      if (currentUser && !isVendorFlow) {
        res = await apiFetch("/tprm/submit", {
          method: "POST",
          body: JSON.stringify({
            answers,
            department_id: activeDepartmentId,
            cycle_id: currentCycleId || null,
          }),
        });
      } else if (isVendorFlow) {
        res = await fetch(`${API}/vendor-auth/questionnaire/submit`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tenant_slug: slug, vendor_id: id, answers }),
        });
      } else {
        setError("Please log in to submit the assessment");
        return;
      }

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(parseApiError(data, "Submission failed — please try again"));
        return;
      }

      setSubmitResult({
        overall_score: data.overall_score ?? 0,
        section_scores: data.section_scores || {},
        department_name:
          data.department_name ||
          departments.find((d) => d.id === activeDepartmentId)?.name ||
          "",
        answered: answeredCount,
        total: totalQuestions,
      });

      if (isVendorFlow) {
        navigate("/vendor/documents", { state: { task_id: data.task_id } });
      } else {
        setSubmitted(true);
      }
    } catch {
      setError("Network error");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <TprmSubmitResultScreen
        submitResult={submitResult}
        departmentLabel={submitResult?.department_name || ""}
        onViewSubmissions={() => navigate("/user/tprm/submissions")}
      />
    );
  }

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        height: "100%",
        overflow: "hidden",
        background: "#f8fafc",
        fontFamily: "'DM Sans',system-ui,-apple-system,sans-serif",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        .thin-scrollbar::-webkit-scrollbar { width: 3px; }
        .thin-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .thin-scrollbar::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.28); border-radius: 999px; }
        .thin-scrollbar { scrollbar-width: thin; scrollbar-color: rgba(99,102,241,0.28) transparent; }
        button { font-family: inherit; cursor: pointer; -webkit-appearance: none; appearance: none; }
        input, textarea, select { font-family: inherit; box-sizing: border-box; }
        input[type="date"]::-webkit-calendar-picker-indicator { opacity: 0.5; }
        button:focus-visible, input:focus-visible, textarea:focus-visible, select:focus-visible {
          outline: 2px solid rgba(99,102,241,0.6); outline-offset: 1px;
        }
        button:focus:not(:focus-visible), input:focus:not(:focus-visible),
        textarea:focus:not(:focus-visible), select:focus:not(:focus-visible) { outline: none; }
        .gradient-text {
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent; color: transparent;
        }
        @media (max-width: 1024px) {
          .tprm-desktop-sidebar { display: none !important; }
        }
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after { transition-duration: 0.01ms !important; animation-duration: 0.01ms !important; }
        }
      `}</style>

      {/* ΓöÇΓöÇ Slim header — PIA style ΓöÇΓöÇ */}
      <header
        data-tour-id="tour-tprm-header"
        style={{
          background: "#ffffff",
          boxShadow:
            "0 1px 0 0 rgba(99,102,241,0.18),0 2px 8px rgba(0,0,0,0.04)",
          borderBottom: "1px solid rgba(99,102,241,0.10)",
          padding: "10px 20px",
          display: "flex",
          alignItems: "center",
          gap: 16,
          flexShrink: 0,
        }}
      >
        {/* Brand */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              background:
                "linear-gradient(135deg,#6366f1 0%,#8b5cf6 50%,#a855f7 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow:
                "0 4px 18px rgba(99,102,241,0.55),0 2px 8px rgba(139,92,246,0.35)",
            }}
          >
            <ShieldCheck size={16} color="#fff" />
          </div>
          <div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: "#0f172a",
                lineHeight: 1.2,
              }}
            >
              TPRM
            </div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 500,
                color: "#1e293b",
                lineHeight: 1.2,
              }}
            >
              TPRM Assessment
            </div>
          </div>
        </div>
        {/* Centre — department selector for tenant users */}
        <div
          data-tour-id="tour-tprm-department"
          style={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            minWidth: 0,
          }}
        >
          {currentUser && !isVendorFlow ? (
            <DepartmentDropdown
              value={departmentId}
              onChange={handleDepartmentChange}
              departments={departments}
              loading={departmentsLoading}
              lockToAssigned
              style={{ flex: "1 1 200px", maxWidth: 320 }}
            />
          ) : (
            <span
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: "#1e293b",
                padding: "5px 14px",
                borderRadius: 9,
                background: "#f8fafc",
                border: "1px solid rgba(99,102,241,0.12)",
                whiteSpace: "nowrap",
              }}
            >
              Third-Party Risk Management · Structured Questionnaire
            </span>
          )}
          {isLocked && currentUser && !isVendorFlow && departmentId && (
            <span
              title="This department's TPRM assessment has already been submitted and is read-only"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                marginLeft: 8,
                padding: "5px 10px",
                borderRadius: 999,
                background: "linear-gradient(135deg,#f8fafc,#eef2f7)",
                border: "1px solid #cbd5e1",
                color: "#475569",
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: 0.2,
                flexShrink: 0,
              }}
            >
              <Lock size={11} /> Locked
            </span>
          )}
        </div>
        <button
          data-tour-id="tour-tprm-view-table"
          onClick={() => navigate("/user/tprm/submissions")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "7px 14px",
            borderRadius: 10,
            background: "#ffffff",
            color: "#6366f1",
            fontSize: 11,
            fontWeight: 600,
            border: "1px solid rgba(99,102,241,0.25)",
            boxShadow: "0 2px 8px rgba(99,102,241,0.1)",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <LayoutList size={13} /> View Table
        </button>
        {/* Progress bar */}
        <div
          data-tour-id="tour-tprm-draft"
          style={{ flexShrink: 0, minWidth: 220 }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 10,
              color: "#334155",
              marginBottom: 4,
            }}
          >
            <span>Overall Progress</span>
            <span style={{ fontWeight: 700, color: "#0f172a" }}>
              {answeredCount}/{totalQuestions} · {progress}%
            </span>
          </div>
          <div
            style={{
              height: 6,
              background: "#e2e8f0",
              borderRadius: 999,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${progress}%`,
                background: "linear-gradient(90deg,#6366f1,#818cf8)",
                borderRadius: 999,
                transition: "width 0.5s ease",
              }}
            />
          </div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 500,
              color: "#475569",
              marginTop: 3,
            }}
          >
            {draftLoading
              ? "Loading…"
              : draftSaving
                ? "Saving draft…"
                : lastSavedAt
                  ? `Draft saved · ${new Date(lastSavedAt).toLocaleString()}`
                  : "Progress is auto-saved"}
          </div>
        </div>
      </header>

      {error && (
        <div
          style={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: "8px 16px",
            background: "#fef2f2",
            borderBottom: "1px solid #fecaca",
            fontSize: 11.5,
            color: "#b91c1c",
            textAlign: "center",
          }}
        >
          <AlertTriangle size={14} />
          {error}
        </div>
      )}

      {currentUser && !isVendorFlow && !departmentId && (
        <div
          style={{
            flexShrink: 0,
            padding: "8px 16px",
            background: "#fffbeb",
            borderBottom: "1px solid rgba(245,158,11,0.2)",
            fontSize: 11,
            color: "#92400e",
            textAlign: "center",
          }}
        >
          Select a department above — each department has its own TPRM draft,
          attachments, and submission.
        </div>
      )}

      {isLocked && currentUser && !isVendorFlow && departmentId && (
        <div
          style={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: "8px 16px",
            background: "#f8fafc",
            borderBottom: "1px solid #cbd5e1",
            fontSize: 11.5,
            color: "#475569",
            textAlign: "center",
          }}
        >
          <Lock size={12} />
          This TPRM assessment has already been submitted for this department.
          You&rsquo;re viewing the submitted responses in read-only mode.
          {lockInfo?.submittedAt && (
            <span style={{ color: "#94a3b8" }}>
              &nbsp;· Submitted{" "}
              {new Date(lockInfo.submittedAt).toLocaleDateString()}
            </span>
          )}
        </div>
      )}

      {!isLocked && isCycleClosed && currentUser && !isVendorFlow && departmentId && (
        <div
          style={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: "8px 16px",
            background: "#f8fafc",
            borderBottom: "1px solid #cbd5e1",
            fontSize: 11.5,
            color: "#475569",
            textAlign: "center",
          }}
        >
          <Lock size={12} />
          {currentCycle?.name
            ? `${currentCycle.name} is`
            : "This assessment cycle is"}{" "}
          closed. The form is read-only until your tenant admin reopens the
          cycle.
        </div>
      )}

      {/* ΓöÇΓöÇ Main content ΓöÇΓöÇ */}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {currentUser && !isVendorFlow && !activeDepartmentId ? (
          <DepartmentSelectionRequired message="Please select a department from the dropdown in the header to load, view, or fill the TPRM form." />
        ) : (
          <>
            {loading ? (
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 12,
                  background: "#ffffff",
                }}
              >
                <Loader2 size={28} className="animate-spin text-indigo-600" />
                <p style={{ color: "#1e293b", fontSize: 14, fontWeight: 500 }}>
                  Loading questionnaire…
                </p>
              </div>
            ) : sections.length === 0 ? (
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#ffffff",
                }}
              >
                <p style={{ color: "#334155", fontSize: 14, fontWeight: 500 }}>
                  {currentUser || isVendorFlow
                    ? "Could not load questionnaire. Restart the backend and try again."
                    : "Please log in to load the TPRM questionnaire."}
                </p>
              </div>
            ) : (
              <>
                {/* ΓöÇΓöÇ Sidebar ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ */}
                <aside
                  data-tour-id="tour-tprm-sidebar"
                  className="tprm-desktop-sidebar"
                  style={{
                    width: 260,
                    flexShrink: 0,
                    alignSelf: "stretch",
                    background: "#ffffff",
                    borderRight: "1px solid rgba(99,102,241,0.14)",
                    boxShadow: "2px 0 12px rgba(99,102,241,0.06)",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                  }}
                >
                  {/* ΓöÇΓöÇ Overall Score Card ΓöÇΓöÇ */}
                  <div
                    data-tour-id="tour-tprm-score"
                    style={{
                      padding: "14px 14px 12px",
                      borderBottom: "1px solid #f1f5f9",
                      flexShrink: 0,
                    }}
                  >
                    <p
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#334155",
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        marginBottom: 10,
                      }}
                    >
                      Overall Score
                    </p>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      <ScoreArc score={overallScore} />
                      <div style={{ flex: 1 }}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            fontSize: 10.5,
                            fontWeight: 700,
                            padding: "3px 10px",
                            borderRadius: 20,
                            marginBottom: 8,
                            background: overallBadge.bg.replace("bg-", ""),
                            color: "#1e293b",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${overallBadge.dot}`}
                          />
                          {overallBadge.label}
                        </span>
                        {[
                          {
                            label: "Answered",
                            value: answeredCount,
                            color: "#10b981",
                          },
                          {
                            label: "Remaining",
                            value: totalQuestions - answeredCount,
                            color: "#64748b",
                          },
                        ].map((x) => (
                          <div
                            key={x.label}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              marginBottom: 3,
                            }}
                          >
                            <div
                              style={{
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                background: x.color,
                                flexShrink: 0,
                              }}
                            />
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 500,
                                color: "#334155",
                                flex: 1,
                              }}
                            >
                              {x.label}
                            </span>
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#1e293b",
                              }}
                            >
                              {x.value}
                            </span>
                          </div>
                        ))}
                        <div
                          style={{
                            height: 5,
                            borderRadius: 999,
                            background: "#e2e8f0",
                            overflow: "hidden",
                            marginTop: 6,
                          }}
                        >
                          <div
                            style={{
                              height: "100%",
                              width: `${progress}%`,
                              borderRadius: 999,
                              background:
                                "linear-gradient(90deg,#6366f1,#818cf8)",
                              transition: "width 0.7s ease",
                            }}
                          />
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginTop: 4,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 500,
                              color: "#334155",
                            }}
                          >
                            Completion
                          </span>
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: "#0f172a",
                            }}
                          >
                            {progress}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ΓöÇΓöÇ Section Navigation ΓöÇΓöÇ */}
                  <div
                    data-tour-id="tour-tprm-sections"
                    style={{ padding: "10px 12px 6px", flexShrink: 0 }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                      }}
                    >
                      <Award size={10} color="#334155" />
                      <p
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#0f172a",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                        }}
                      >
                        Sections
                      </p>
                    </div>
                  </div>

                  <div
                    style={{
                      flex: 1,
                      minHeight: 0,
                      overflowY: "auto",
                      padding: "0 8px 10px",
                      scrollbarWidth: "thin",
                      scrollbarColor: "rgba(99,102,241,0.28) transparent",
                    }}
                    className="thin-scrollbar"
                  >
                    {sections.map((sec, i) => {
                      const Icon = sec.icon;
                      const secAnswered = sec.questions.filter((q) =>
                        isAnswered(answers[q.id]),
                      ).length;
                      const secTotal = sec.questions.length;
                      const isComplete = secAnswered === secTotal;
                      const isActive = i === step;
                      const secPct = secTotal
                        ? Math.round((secAnswered / secTotal) * 100)
                        : 0;

                      return (
                        <button
                          key={sec.id}
                          onClick={() => setStep(i)}
                          style={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            gap: 5,
                            padding: "9px 10px",
                            borderRadius: 12,
                            textAlign: "left",
                            border: `1px solid ${isActive ? "rgba(99,102,241,0.3)" : "transparent"}`,
                            background: isActive
                              ? "linear-gradient(135deg,#6366f1 0%,#818cf8 100%)"
                              : "transparent",
                            cursor: "pointer",
                            transition: "all 0.15s ease",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <Icon
                              size={13}
                              color={isActive ? "#ffffff" : "#0f172a"}
                            />
                            <span
                              style={{
                                fontSize: 11.5,
                                fontWeight: 600,
                                flex: 1,
                                color: isActive ? "#ffffff" : "#0f172a",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {sec.title}
                            </span>
                            {isComplete ? (
                              <CheckCircle2
                                size={12}
                                color={isActive ? "#c7d2fe" : "#10b981"}
                              />
                            ) : (
                              <span
                                style={{
                                  fontSize: 10,
                                  fontWeight: 600,
                                  color: isActive
                                    ? "rgba(255,255,255,0.9)"
                                    : "#1e293b",
                                }}
                              >
                                {secAnswered}/{secTotal}
                              </span>
                            )}
                          </div>
                          <div
                            style={{
                              height: 4,
                              borderRadius: 999,
                              background: isActive
                                ? "rgba(99,102,241,0.3)"
                                : "#f1f5f9",
                            }}
                          >
                            <div
                              style={{
                                height: "100%",
                                borderRadius: 999,
                                transition: "width 0.5s ease",
                                background: isActive
                                  ? "#ffffff"
                                  : isComplete
                                    ? "#34d399"
                                    : "#818cf8",
                                width: `${secPct}%`,
                              }}
                            />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </aside>

                {/* ΓöÇΓöÇ Question Panel ΓöÇΓöÇ */}
                <main
                  ref={questionScrollRef}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflowY: "auto",
                    height: "100%",
                    background: "#ffffff",
                    scrollbarWidth: "thin",
                    scrollbarColor: "rgba(99,102,241,0.3) #e2e8f0",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      padding: "20px 24px 40px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 16,
                    }}
                  >
                    {/* Section banner */}
                    <div
                      data-tour-id="tour-tprm-section-banner"
                      style={{
                        borderRadius: 20,
                        padding: "18px 20px",
                        background: "#ffffff",
                        border: "1px solid #e2e8f0",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 14,
                        }}
                      >
                        <div
                          style={{
                            width: 46,
                            height: 46,
                            borderRadius: 14,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            background: `linear-gradient(135deg, ${currentSection.accent}ee 0%, ${currentSection.accent}88 100%)`,
                            border: `1px solid ${currentSection.accent}40`,
                            boxShadow: `0 4px 16px ${currentSection.accent}40`,
                          }}
                        >
                          <currentSection.icon size={20} color="#fff" />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              flexWrap: "wrap",
                            }}
                          >
                            <h2
                              style={{
                                fontSize: 16,
                                fontWeight: 700,
                                color: "#0f172a",
                                margin: 0,
                              }}
                            >
                              {currentSection.title}
                            </h2>
                            {isLocked && (
                              <span
                                style={{
                                  fontSize: 10,
                                  fontWeight: 700,
                                  padding: "2px 8px",
                                  borderRadius: 6,
                                  background: "#f1f5f9",
                                  color: "#64748b",
                                  border: "1px solid #cbd5e1",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 4,
                                }}
                              >
                                <Lock size={9} /> View Only
                              </span>
                            )}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 16,
                              marginTop: 10,
                              flexWrap: "wrap",
                            }}
                          >
                            <span
                              style={{
                                fontSize: 11,
                                color: "#1e293b",
                                display: "flex",
                                alignItems: "center",
                                gap: 5,
                              }}
                            >
                              <ClipboardList size={11} /> {secTotal} questions
                            </span>
                            <span
                              style={{
                                fontSize: 11,
                                color: "#1e293b",
                                display: "flex",
                                alignItems: "center",
                                gap: 5,
                              }}
                            >
                              <TrendingUp size={11} /> Section {step + 1} of{" "}
                              {sections.length}
                            </span>
                            <div
                              style={{
                                marginLeft: "auto",
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: currentSection.accent,
                                }}
                              >
                                {secPct}%
                              </span>
                              <div
                                style={{
                                  width: 80,
                                  height: 5,
                                  borderRadius: 9,
                                  background: "#f1f5f9",
                                  overflow: "hidden",
                                }}
                              >
                                <div
                                  style={{
                                    width: `${secPct}%`,
                                    height: "100%",
                                    background: currentSection.accent,
                                    borderRadius: 9,
                                    transition: "width 0.7s ease",
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Questions */}
                    <div
                      style={{ position: "relative" }}
                    >
                      {isLocked && (
                        <div
                          aria-hidden="true"
                          style={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 5,
                            cursor: "not-allowed",
                            background:
                              "repeating-linear-gradient(135deg, rgba(148,163,184,0.05) 0px, rgba(148,163,184,0.05) 14px, rgba(148,163,184,0.09) 14px, rgba(148,163,184,0.09) 28px)",
                            borderRadius: 16,
                          }}
                        />
                      )}
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 12,
                          pointerEvents: formReadOnly ? "none" : "auto",
                          opacity: formReadOnly ? 0.72 : 1,
                          filter: formReadOnly
                            ? "grayscale(0.25) saturate(0.85)"
                            : "none",
                          userSelect: formReadOnly ? "text" : "auto",
                          transition: "opacity 0.2s ease, filter 0.2s ease",
                        }}
                      >
                        {currentSection.questions.map((q, qi) => {
                          const QIcon = getQuestionIcon(q.type);
                          const answered = isAnswered(answers[q.id]);
                          return (
                            <div
                              key={q.id}
                              id={`question-${q.id}`}
                              data-question-id={q.id}
                              data-field-wrapper
                              data-tour-id={qi === 0 ? "tour-tprm-questions" : undefined}
                              style={{
                                borderRadius: 18,
                                overflow: "hidden",
                                background: "#ffffff",
                                border: invalidQuestions.includes(q.id)
                                  ? "2px solid #ef4444"
                                  : answered
                                    ? `1px solid ${currentSection.accent}55`
                                    : "1px solid rgba(99,102,241,0.12)",
                                boxShadow: answered
                                  ? `0 2px 12px ${currentSection.accent}18`
                                  : "0 1px 3px rgba(0,0,0,0.02)",
                                transition: "all 0.2s ease-in-out",
                              }}
                            >
                              <div style={{ padding: "16px 18px" }}>
                              {invalidQuestions.includes(q.id) && (
                                <p
                                  style={{
                                    ...questionHintStyle,
                                    marginBottom: 12,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                  }}
                                >
                                  <AlertTriangle size={14} color="#ef4444" />
                                  {!answered
                                    ? "This question is required. Please provide an answer."
                                    : "An attachment is required for this question."}
                                </p>
                              )}
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 12,
                                  marginBottom: 12,
                                }}
                              >
                                <div
                                  style={{
                                    width: 26,
                                    height: 26,
                                    borderRadius: 8,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    background: `linear-gradient(135deg, ${currentSection.accent}28 0%, ${currentSection.accent}14 100%)`,
                                    border: `1px solid ${currentSection.accent}35`,
                                    fontSize: 11,
                                    fontWeight: 800,
                                    color: currentSection.accent,
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {qi + 1}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div
                                    style={{
                                      fontSize: 13,
                                      fontWeight: 500,
                                      color: "#0f172a",
                                      lineHeight: 1.55,
                                      display: "flex",
                                      alignItems: "flex-start",
                                      gap: 6,
                                    }}
                                  >
                                    <QIcon
                                      size={14}
                                      style={{
                                        color: currentSection.accent,
                                        flexShrink: 0,
                                        marginTop: 2,
                                      }}
                                    />
                                    <span>
                                      {q.text}
                                      {isFieldRequired(q.required, false) && (
                                        <span style={{ color: "#ef4444" }}>
                                          {" "}
                                          *
                                        </span>
                                      )}
                                    </span>
                                    {(referenceEvidence[q.id] ||
                                      referenceEvidence[q.external_id]) && (
                                      <SuggestedEvidenceTrigger
                                        evidence={
                                          referenceEvidence[q.id] ||
                                          referenceEvidence[q.external_id]
                                        }
                                        usingReference={!!usingReferenceFor[qKey(q.id)]}
                                        onUseReference={(evidence) =>
                                          attachSuggestedEvidence(q.id, evidence)
                                        }
                                        attachDisabledReason={
                                          currentUser && !isVendorFlow && !activeDepartmentId
                                            ? "Select a department before attaching evidence."
                                            : ""
                                        }
                                        disabled={formReadOnly || !!uploadingFor[qKey(q.id)]}
                                        style={{ marginLeft: 4 }}
                                      />
                                    )}
                                  </div>
                                </div>
                              </div>

                              {q.type === "boolean" && (
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                                    gap: 8,
                                  }}
                                >
                                  {["Yes", "No"].map((opt) => {
                                    const cfg = BOOL_CFG[opt];
                                    const Ico = cfg.icon;
                                    const val = opt === "Yes";
                                    const sel = answers[q.id] === val;
                                    return (
                                      <button
                                        key={opt}
                                        type="button"
                                        onClick={() =>
                                          handleAnswer(
                                            q.id,
                                            sel ? undefined : val,
                                          )
                                        }
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 10,
                                          padding: "10px 12px",
                                          borderRadius: 12,
                                          border: `1px solid ${sel ? cfg.border : "rgba(99,102,241,0.1)"}`,
                                          background: sel ? cfg.bg : "#ffffff",
                                          color: sel ? cfg.color : "#334155",
                                          cursor: "pointer",
                                          textAlign: "left",
                                          fontFamily: "inherit",
                                          transition: "all 0.15s",
                                        }}
                                      >
                                        <div
                                          style={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: 7,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexShrink: 0,
                                            background: sel
                                              ? cfg.color
                                              : "#ffffff",
                                            border: `1px solid ${sel ? cfg.color : "rgba(99,102,241,0.08)"}`,
                                          }}
                                        >
                                          <Ico
                                            size={12}
                                            color={sel ? "#fff" : "#475569"}
                                          />
                                        </div>
                                        <span
                                          style={{
                                            fontSize: 11,
                                            fontWeight: 600,
                                          }}
                                        >
                                          {opt}
                                        </span>
                                      </button>
                                    );
                                  })}
                                </div>
                              )}

                              {q.type === "select" && (
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                                    gap: 8,
                                  }}
                                >
                                  {(q.options || []).map((opt, oi) => {
                                    const value = getOptionValue(opt);
                                    const label = getOptionLabel(opt);
                                    const sel = answers[q.id] === value;
                                    return (
                                      <button
                                        key={oi}
                                        type="button"
                                        onClick={() =>
                                          handleAnswer(
                                            q.id,
                                            sel ? undefined : value,
                                          )
                                        }
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 10,
                                          padding: "10px 12px",
                                          borderRadius: 12,
                                          border: `1px solid ${sel ? currentSection.accent : "rgba(99,102,241,0.1)"}`,
                                          background: sel
                                            ? `${currentSection.accent}12`
                                            : "#ffffff",
                                          color: sel ? currentSection.accent : "#334155",
                                          cursor: "pointer",
                                          textAlign: "left",
                                          fontFamily: "inherit",
                                          fontSize: 11,
                                          fontWeight: sel ? 600 : 500,
                                          transition: "all 0.15s",
                                        }}
                                      >
                                        <div
                                          style={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: "50%",
                                            flexShrink: 0,
                                            background: sel
                                              ? currentSection.accent
                                              : "#cbd5e1",
                                          }}
                                        />
                                        {label}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}

                              {q.type === "multiselect" && (
                                <div
                                  style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 8,
                                  }}
                                >
                                  {(q.options || []).map((opt, oi) => {
                                    const value = getOptionValue(opt);
                                    const label = getOptionLabel(opt);
                                    const selected = (
                                      answers[q.id] || []
                                    ).includes(value);
                                    return (
                                      <button
                                        key={oi}
                                        type="button"
                                        onClick={() => {
                                          const current = answers[q.id] || [];
                                          handleAnswer(
                                            q.id,
                                            selected
                                              ? current.filter(
                                                  (x) => x !== value,
                                                )
                                              : [...current, value],
                                          );
                                        }}
                                        style={{
                                          padding: "7px 14px",
                                          borderRadius: 20,
                                          fontSize: 11,
                                          fontWeight: 500,
                                          background: selected
                                            ? `${currentSection.accent}18`
                                            : "#f8fafc",
                                          border: `1px solid ${selected ? currentSection.accent : "rgba(99,102,241,0.15)"}`,
                                          color: selected
                                            ? currentSection.accent
                                            : "#334155",
                                          cursor: "pointer",
                                          fontFamily: "inherit",
                                        }}
                                      >
                                        {label}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}

                              {q.type === "rating" && (
                                <div
                                  style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
                                >
                                  {[1, 2, 3, 4, 5].map((n) => {
                                    const sel = answers[q.id] === n;
                                    return (
                                      <button
                                        key={n}
                                        type="button"
                                        onClick={() =>
                                          handleAnswer(q.id, sel ? undefined : n)
                                        }
                                        style={{
                                          width: 36,
                                          height: 36,
                                          borderRadius: 10,
                                          fontSize: 12,
                                          fontWeight: 700,
                                          background: sel
                                            ? currentSection.accent
                                            : "#f8fafc",
                                          border: `1px solid ${sel ? currentSection.accent : "rgba(99,102,241,0.15)"}`,
                                          color: sel ? "#fff" : "#334155",
                                          cursor: "pointer",
                                          fontFamily: "inherit",
                                        }}
                                      >
                                        {n}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}

                              {q.type === "text" && (
                                <input
                                  type="text"
                                  value={answers[q.id] || ""}
                                  onChange={(e) =>
                                    handleAnswer(q.id, e.target.value)
                                  }
                                  placeholder="Type your answer…"
                                  style={{
                                    width: "100%",
                                    fontSize: 12,
                                    border: "1px solid rgba(99,102,241,0.2)",
                                    borderRadius: 10,
                                    padding: "9px 12px",
                                    outline: "none",
                                    fontFamily: "inherit",
                                    background: "#ffffff",
                                    color: "#0f172a",
                                  }}
                                />
                              )}

                              {q.type === "textarea" && (
                                <textarea
                                  rows={4}
                                  value={answers[q.id] || ""}
                                  onChange={(e) =>
                                    handleAnswer(q.id, e.target.value)
                                  }
                                  placeholder="Describe in detail…"
                                  style={{
                                    width: "100%",
                                    fontSize: 12,
                                    border: "1px solid rgba(99,102,241,0.2)",
                                    borderRadius: 10,
                                    padding: "9px 12px",
                                    resize: "vertical",
                                    outline: "none",
                                    fontFamily: "inherit",
                                    background: "#ffffff",
                                    color: "#0f172a",
                                  }}
                                />
                              )}

                              {q.type === "date" && (
                                <input
                                  type="date"
                                  value={answers[q.id] || ""}
                                  onChange={(e) =>
                                    handleAnswer(q.id, e.target.value)
                                  }
                                  style={{
                                    width: "100%",
                                    fontSize: 12,
                                    border: "1px solid rgba(99,102,241,0.2)",
                                    borderRadius: 10,
                                    padding: "9px 12px",
                                    outline: "none",
                                    fontFamily: "inherit",
                                    background: "#ffffff",
                                    color: "#0f172a",
                                  }}
                                />
                              )}

                              {q.allow_attachment && (
                                <TprmQuestionAttachment
                                  questionId={q.id}
                                  attachments={attachments[qKey(q.id)] || []}
                                  uploading={!!uploadingFor[qKey(q.id)]}
                                  onUpload={uploadAttachment}
                                  onRemove={removeAttachment}
                                  removingId={removingAttachmentId}
                                  attachmentRequired={q.attachment_required}
                                  disabled={
                                    formReadOnly ||
                                    (currentUser &&
                                      !isVendorFlow &&
                                      !activeDepartmentId)
                                  }
                                  error={attachmentErrors[qKey(q.id)] || ""}
                                />
                              )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {isLocked && (
                        <div
                          style={{
                            position: "sticky",
                            bottom: 12,
                            display: "flex",
                            justifyContent: "center",
                            marginTop: 14,
                            pointerEvents: "none",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 7,
                              padding: "7px 16px",
                              borderRadius: 999,
                              background: "rgba(15,23,42,0.85)",
                              color: "#f8fafc",
                              fontSize: 11,
                              fontWeight: 600,
                              boxShadow: "0 6px 20px rgba(15,23,42,0.25)",
                              backdropFilter: "blur(4px)",
                            }}
                          >
                            <Lock size={12} /> Submitted — read only
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Footer navigation */}
                    <div
                      data-tour-id="tour-tprm-nav-footer"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginTop: 8,
                        paddingTop: 16,
                        borderTop: "1px solid rgba(99,102,241,0.1)",
                      }}
                    >
                      <button
                        type="button"
                        data-tour-id="tour-tprm-prev"
                        onClick={() => setStep((s) => Math.max(0, s - 1))}
                        disabled={step === 0}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 7,
                          padding: "9px 18px",
                          borderRadius: 12,
                          background: "#ffffff",
                          border: "1px solid rgba(99,102,241,0.18)",
                          color: step === 0 ? "#475569" : "#334155",
                          fontSize: 12,
                          fontWeight: 500,
                          cursor: step === 0 ? "not-allowed" : "pointer",
                          opacity: step === 0 ? 0.4 : 1,
                          fontFamily: "inherit",
                        }}
                      >
                        <ChevronLeft size={15} /> Previous
                      </button>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                          maxWidth: "100%",
                          overflowX: "auto",
                          paddingBottom: 2,
                        }}
                      >
                        {sections.map((s, i) => (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => setStep(i)}
                            style={{
                              height: 6,
                              borderRadius: 9,
                              border: "none",
                              cursor: "pointer",
                              width: i === step ? 22 : 6,
                              background:
                                i === step
                                  ? currentSection.accent
                                  : i < step
                                    ? `${currentSection.accent}55`
                                    : "rgba(99,102,241,0.2)",
                              transition: "width 0.2s ease",
                            }}
                          />
                        ))}
                      </div>

                      {formReadOnly ? (
                        step < sections.length - 1 ? (
                          <button
                            type="button"
                            onClick={handleNext}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 7,
                              padding: "9px 18px",
                              borderRadius: 12,
                              background: "#ffffff",
                              color: "#334155",
                              fontSize: 12,
                              fontWeight: 600,
                              border: "1px solid rgba(99,102,241,0.18)",
                              cursor: "pointer",
                              fontFamily: "inherit",
                            }}
                          >
                            Next <ChevronRight size={15} />
                          </button>
                        ) : (
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 7,
                              padding: "9px 20px",
                              borderRadius: 12,
                              background: "#f1f5f9",
                              color: "#64748b",
                              fontSize: 12,
                              fontWeight: 700,
                              border: "1px solid #cbd5e1",
                            }}
                          >
                            <Lock size={14} /> Locked — Already Submitted
                          </span>
                        )
                      ) : step < sections.length - 1 ? (
                        <button
                          type="button"
                          data-tour-id="tour-tprm-next"
                          onClick={handleNext}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 7,
                            padding: "9px 18px",
                            borderRadius: 12,
                            background: `linear-gradient(135deg, ${currentSection.accent}, ${currentSection.accent}bb)`,
                            color: "#fff",
                            fontSize: 12,
                            fontWeight: 600,
                            border: "none",
                            boxShadow: `0 4px 16px ${currentSection.accent}40`,
                            cursor: "pointer",
                            fontFamily: "inherit",
                          }}
                        >
                          Next <ChevronRight size={15} />
                        </button>
                      ) : (
                        <button
                          type="button"
                          data-tour-id="tour-tprm-submit"
                          onClick={handleSubmit}
                          disabled={submitting || answeredCount === 0}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 7,
                            padding: "9px 20px",
                            borderRadius: 12,
                            background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                            color: "#fff",
                            fontSize: 12,
                            fontWeight: 700,
                            border: "none",
                            boxShadow: "0 4px 20px rgba(99,102,241,0.45)",
                            cursor:
                              submitting || answeredCount === 0
                                ? "not-allowed"
                                : "pointer",
                            opacity: submitting || answeredCount === 0 ? 0.7 : 1,
                            fontFamily: "inherit",
                          }}
                        >
                          {submitting ? (
                            <>
                              <Loader2 size={14} className="animate-spin" />{" "}
                              Submitting…
                            </>
                          ) : (
                            <>
                              <Zap size={14} /> Submit Assessment
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </main>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
